import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: -20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
};

export const TwitchLive = ({ channel = "tylegn" }) => {
  const [isLive, setIsLive] = useState(false);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const embedRef = useRef(null);
  const playerRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Load Twitch Embed script
    const existingScript = document.querySelector('script[src="https://embed.twitch.tv/embed/v1.js"]');
    
    const initEmbed = () => {
      if (!window.Twitch || !window.Twitch.Embed || !embedRef.current) return;
      
      // Clear any existing embed
      if (embedRef.current) {
        embedRef.current.innerHTML = '';
      }

      try {
        const hostname = window.location.hostname;
        const embed = new window.Twitch.Embed(`twitch-embed-${channel}`, {
          width: '100%',
          height: '100%',
          channel: channel,
          layout: 'video',
          autoplay: true,
          muted: true,
          parent: [hostname, 'localhost'],
          allowfullscreen: false,
        });

        embed.addEventListener(window.Twitch.Embed.VIDEO_READY, () => {
          playerRef.current = embed.getPlayer();
          setEmbedLoaded(true);
          
          // Check live status periodically
          const checkLive = () => {
            try {
              if (playerRef.current) {
                const stats = playerRef.current.getPlaybackStats();
                // If there are playback stats and it's broadcasting, it's live
                if (stats && stats.backendVersion) {
                  setIsLive(true);
                } else {
                  // Double check: if the player has a channel and video is playing
                  const isPaused = playerRef.current.isPaused();
                  if (!isPaused) {
                    setIsLive(true);
                  }
                }
              }
            } catch (e) {
              // Silently handle - player might not be ready
            }
          };

          // Initial check after a short delay
          setTimeout(checkLive, 3000);
          
          // Keep checking periodically
          checkIntervalRef.current = setInterval(checkLive, 30000);
        });

        embed.addEventListener(window.Twitch.Embed.VIDEO_PLAY, () => {
          // When video actually starts playing, the channel is live
          setIsLive(true);
        });

      } catch (e) {
        console.error('Twitch embed error:', e);
      }
    };

    if (existingScript) {
      // Script already loaded
      if (window.Twitch && window.Twitch.Embed) {
        initEmbed();
      } else {
        existingScript.addEventListener('load', initEmbed);
      }
    } else {
      const script = document.createElement('script');
      script.src = 'https://embed.twitch.tv/embed/v1.js';
      script.async = true;
      script.onload = initEmbed;
      document.body.appendChild(script);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [channel]);

  return (
    <AnimatePresence>
      {isLive && (
        <motion.section
          variants={reveal}
          initial="hidden"
          animate="show"
          exit="exit"
          className="py-8 md:py-12 scroll-mt-24"
          data-testid="twitch-live-section"
        >
          <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <span className="mono-label text-red-400">Live Now</span>
              </div>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                twitch.tv/{channel}
              </Badge>
              <a
                href={`https://twitch.tv/${channel}`}
                target="_blank"
                rel="noreferrer"
                className="ml-auto text-sm text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-1"
                data-testid="twitch-external-link"
              >
                Watch on Twitch
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <Card className="overflow-hidden border-border bg-card" style={{ boxShadow: '0 0 30px rgba(145, 70, 255, 0.15)' }}>
              <div className="aspect-video w-full">
                <div
                  id={`twitch-embed-${channel}`}
                  ref={embedRef}
                  className="w-full h-full"
                />
              </div>
            </Card>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );

  // Hidden embed for checking live status (renders even when not visible)
};

// Lightweight version that just checks if live without showing full embed until confirmed
export const TwitchLiveCheck = ({ channel = "tylegn", children }) => {
  const [isLive, setIsLive] = useState(false);
  const embedRef = useRef(null);
  const checkRef = useRef(null);

  useEffect(() => {
    // Create a hidden container for the initial check
    const container = document.createElement('div');
    container.id = `twitch-check-${channel}`;
    container.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
    document.body.appendChild(container);

    const initCheck = () => {
      if (!window.Twitch || !window.Twitch.Embed) return;

      try {
        const hostname = window.location.hostname;
        const embed = new window.Twitch.Embed(`twitch-check-${channel}`, {
          width: 1,
          height: 1,
          channel: channel,
          layout: 'video',
          autoplay: false,
          muted: true,
          parent: [hostname, 'localhost'],
        });

        embed.addEventListener(window.Twitch.Embed.VIDEO_PLAY, () => {
          setIsLive(true);
        });
      } catch (e) {
        console.error('Twitch check error:', e);
      }
    };

    const existingScript = document.querySelector('script[src="https://embed.twitch.tv/embed/v1.js"]');
    if (existingScript && window.Twitch && window.Twitch.Embed) {
      initCheck();
    } else if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://embed.twitch.tv/embed/v1.js';
      script.async = true;
      script.onload = initCheck;
      document.body.appendChild(script);
    } else {
      existingScript.addEventListener('load', initCheck);
    }

    return () => {
      const el = document.getElementById(`twitch-check-${channel}`);
      if (el) el.remove();
    };
  }, [channel]);

  if (!isLive) return null;
  return children;
};
